import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GitPullRequest, CheckCircle2, Clock, RotateCcw, TrendingUp } from "lucide-react";
import { ProposalDiff } from "./ProposalDiff";
import { VersionTimeline } from "./VersionTimeline";
import {
  useSentinelProposals,
  useSentinelVersionHistory,
  useUpdateProposalState,
  useApplyProposal,
} from "@/hooks/useSentinelCycle";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";

interface SentinelProposalsProps {
  orgId?: string;
}

const FILTERS = [
  { value: "all", label: "Todas" },
  { value: "pending", label: "Pendentes" },
  { value: "approved", label: "Aprovadas" },
  { value: "applied", label: "Aplicadas" },
  { value: "reverted", label: "Revertidas" },
] as const;

export default function SentinelProposals({ orgId }: SentinelProposalsProps) {
  const [filter, setFilter] = useState<string>("pending");
  const { data: proposals = [], isLoading } = useSentinelProposals();
  const { data: history = [] } = useSentinelVersionHistory();
  const updateState = useUpdateProposalState();
  const applyProposal = useApplyProposal();

  const filtered = useMemo(() => {
    if (filter === "all") return proposals;
    // "Pendentes" agrega draft|candidate|pending — propostas geradas pelo
    // sentinel-propose nascem como `draft` quando a dimensão não é sensível,
    // então o filtro precisa englobar ambos para não esconder a maioria.
    if (filter === "pending") {
      return proposals.filter((p) => ["draft", "candidate", "pending"].includes(p.state));
    }
    return proposals.filter((p) => p.state === filter);
  }, [proposals, filter]);

  const metrics = useMemo(() => {
    const total = proposals.length;
    const approved = proposals.filter((p) => ["approved", "applied"].includes(p.state)).length;
    const applied = proposals.filter((p) => p.state === "applied").length;
    const reverted = proposals.filter((p) => p.state === "reverted").length;
    const pending = proposals.filter((p) => ["pending", "candidate", "draft"].includes(p.state)).length;

    // Avg approval time (created_at -> approved_at)
    const approvedWithTime = proposals.filter((p) => p.approved_at && p.created_at);
    const avgApprovalMs =
      approvedWithTime.length > 0
        ? approvedWithTime.reduce(
            (sum, p) => sum + (new Date(p.approved_at!).getTime() - new Date(p.created_at).getTime()),
            0,
          ) / approvedWithTime.length
        : 0;
    const avgApprovalH = avgApprovalMs / 3600000;

    const approvalRate = total > 0 ? (approved / total) * 100 : 0;

    return { total, approved, applied, reverted, pending, approvalRate, avgApprovalH };
  }, [proposals]);

  const handleApprove = (id: string) =>
    updateState.mutate({ id, state: "approved", approved_by: "operator" });
  const handleReject = (id: string) => updateState.mutate({ id, state: "rejected" });
  const handleApply = (id: string) => {
    applyProposal.mutate(
      { proposal_id: id },
      {
        onSuccess: (data) => {
          const outcome = (data as { outcome?: string })?.outcome ?? "queued";
          toast.success(`Proposta aplicada: ${outcome}`);
        },
        onError: (e) => toast.error(`Falha ao aplicar: ${(e as Error).message}`),
      },
    );
  };

  return (
    <div className="space-y-4">
      {/* Governance metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard
          icon={<GitPullRequest className="w-4 h-4 text-primary" />}
          label="Total de Propostas"
          value={metrics.total.toString()}
          hint={`${metrics.pending} pendentes`}
        />
        <MetricCard
          icon={<CheckCircle2 className="w-4 h-4 text-success" />}
          label="Taxa de Aprovação"
          value={`${metrics.approvalRate.toFixed(0)}%`}
          hint={`${metrics.approved} aprovadas`}
        />
        <MetricCard
          icon={<Clock className="w-4 h-4 text-muted-foreground" />}
          label="Tempo Médio"
          value={metrics.avgApprovalH > 0 ? `${metrics.avgApprovalH.toFixed(1)}h` : "—"}
          hint="até aprovação"
        />
        <MetricCard
          icon={<RotateCcw className="w-4 h-4 text-destructive" />}
          label="Revertidas"
          value={metrics.reverted.toString()}
          hint={`${metrics.applied} aplicadas`}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Proposals list */}
        <div className="lg:col-span-2 space-y-3">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <GitPullRequest className="w-4 h-4" />
                  Propostas de Mudança
                  <Badge variant="outline" className="ml-2">{filtered.length}</Badge>
                </CardTitle>
              </div>
              <Tabs value={filter} onValueChange={setFilter} className="mt-2">
                <TabsList className="flex-wrap h-auto">
                  {FILTERS.map((f) => (
                    <TabsTrigger key={f.value} value={f.value} className="text-xs">
                      {f.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </CardHeader>
            <CardContent className="space-y-3">
              {isLoading ? (
                <>
                  <Skeleton className="h-32" />
                  <Skeleton className="h-32" />
                </>
              ) : filtered.length === 0 ? (
                <div className="py-12 text-center">
                  <GitPullRequest className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">
                    Nenhuma proposta {filter !== "all" ? `no estado "${filter}"` : ""}.
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Gere propostas a partir de auditorias na aba Auditorias.
                  </p>
                </div>
              ) : (
                filtered.map((p) => (
                  <ProposalDiff
                    key={p.id}
                    proposal={p}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onApply={handleApply}
                    isApplying={applyProposal.isPending}
                  />
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Version timeline */}
        <div className="space-y-3">
          <VersionTimeline entries={history.slice(0, 20)} />
        </div>
      </div>
    </div>
  );
}

function MetricCard({
  icon, label, value, hint,
}: { icon: React.ReactNode; label: string; value: string; hint?: string }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3">
        <div className="flex items-center gap-2 mb-1">
          {icon}
          <span className="text-xs text-muted-foreground">{label}</span>
        </div>
        <div className="text-2xl font-bold">{value}</div>
        {hint && <p className="text-[10px] text-muted-foreground mt-0.5">{hint}</p>}
      </CardContent>
    </Card>
  );
}
